import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import {
  Sparkles,
  ArrowRight,
  RotateCcw,
  CheckCircle2,
  Users,
  Briefcase,
  FileText,
  AlertCircle,
} from 'lucide-react';

/**
 * "Is it for me?" landing-page quiz.
 *
 * 4 questions, auto-advance on selection, progress bar, recommends a plan
 * (Free / Starter / Pro / Enterprise) based on team size + active jobs.
 * Results card has CTA targeting either /signup or mailto for Enterprise.
 *
 * Pure client-side — no analytics, no backend calls.
 */

const QUESTIONS = [
  {
    id: 'team',
    icon: Users,
    label: 'How many recruiters?',
    options: [
      { value: 1, label: 'Just me' },
      { value: 2, label: '2 – 5' },
      { value: 3, label: '6 – 20' },
      { value: 4, label: 'More than 20' },
    ],
  },
  {
    id: 'jobs',
    icon: Briefcase,
    label: 'How many active jobs at any time?',
    options: [
      { value: 1, label: 'Under 5' },
      { value: 2, label: '5 – 25' },
      { value: 3, label: '25 – 100' },
      { value: 4, label: 'More than 100' },
    ],
  },
  {
    id: 'storage',
    icon: FileText,
    label: 'Where do you store resumes today?',
    options: [
      { value: 'excel', label: 'Excel & email attachments' },
      { value: 'drive', label: 'Google Drive / OneDrive' },
      { value: 'ats', label: 'Some other ATS' },
      { value: 'untracked', label: "We don't track them well" },
    ],
  },
  {
    id: 'pain',
    icon: AlertCircle,
    label: 'Biggest pain right now?',
    options: [
      { value: 'lost', label: 'Candidates falling through cracks' },
      { value: 'tat', label: 'Slow turnaround per role' },
      { value: 'reporting', label: 'No reporting on what is working' },
      { value: 'compliance', label: 'Compliance / audit pressure' },
    ],
  },
];

const PLAN_DETAILS = {
  free: {
    label: 'Free',
    color: 'from-slate-500 to-slate-600',
    accent: 'text-slate-300',
    border: 'border-slate-500/40',
    headline: 'You\'ll fit beautifully on Free',
    perks: ['1 user · 3 active jobs · 100 candidates', 'AI resume parsing included', 'No credit card, free forever'],
    cta: 'Start free',
    href: '/signup',
  },
  starter: {
    label: 'Starter',
    color: 'from-sky-400 to-cyan-500',
    accent: 'text-sky-300',
    border: 'border-sky-500/40',
    headline: 'Starter is the right fit',
    perks: ['5 users · 25 active jobs · 5,000 candidates', 'Bulk Excel import + AI matching', '14-day free trial — no card required'],
    cta: 'Start 14-day trial',
    href: '/signup',
  },
  pro: {
    label: 'Pro',
    color: 'from-teal-400 to-emerald-500',
    accent: 'text-teal-300',
    border: 'border-teal-500/40',
    headline: 'Pro is the sweet spot',
    perks: ['20 users · 100 active jobs · 50,000 candidates', 'Recruiter Command Center + "Best 10" AI shortlist', 'Live revenue metrics + bulk re-upload'],
    cta: 'Start 14-day trial',
    href: '/signup',
  },
  enterprise: {
    label: 'Enterprise',
    color: 'from-violet-400 to-fuchsia-500',
    accent: 'text-violet-300',
    border: 'border-violet-500/40',
    headline: "You'll need Enterprise",
    perks: ['Unlimited users / jobs / candidates', 'SSO, dedicated success manager, SLA', 'Custom integrations and white-label'],
    cta: 'Talk to us',
    href: 'mailto:hello@interviewwala.in?subject=Enterprise%20enquiry',
  },
};

const PAIN_REASONING = {
  lost:
    'Our Recruiter Command Center keeps every candidate in one drag-and-drop kanban with full audit trail — nothing slips.',
  tat:
    'Bulk uploads, AI screening and a single-screen workspace shave half a day off every recruiter\'s week.',
  reporting:
    'Live submissions / interviews / closures / TAT metrics on every recruiter\'s home screen — managers see team scope, recruiters see their own.',
  compliance:
    'Every stage transition is logged with the recruiter, timestamp and remarks. Audit trails by default.',
};

const STORAGE_REASONING = {
  excel: 'Migrating from Excel takes a weekend — our bulk import preserves names, contacts, skills, and rejection reasons.',
  drive: 'Resumes attach directly to candidate records via drag-and-drop bulk upload. No more separate Drive folder hunt.',
  ats: 'You can switch in a weekend. Bring your data via bulk import; our pricing is half of what most legacy ATS charge.',
  untracked: 'You\'re probably losing 1-2 placements a month to data chaos. The Free plan alone fixes that.',
};

const computePlan = (answers) => {
  // Use the higher of "team size" and "active jobs" tier — that's the true scale signal
  const tier = Math.max(answers.team || 1, answers.jobs || 1);
  if (tier === 1) return 'free';
  if (tier === 2) return 'starter';
  if (tier === 3) return 'pro';
  return 'enterprise';
};

const LandingQuiz = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0..3 questions, 4 = results
  const [answers, setAnswers] = useState({});

  const progress = step < QUESTIONS.length ? step / QUESTIONS.length : 1;

  const handleSelect = (qId, value) => {
    const next = { ...answers, [qId]: value };
    setAnswers(next);
    // Auto-advance after a brief beat so the selected pill animates first
    setTimeout(() => {
      setStep((s) => s + 1);
    }, 250);
  };

  const reset = () => {
    setAnswers({});
    setStep(0);
  };

  const showResults = step >= QUESTIONS.length;
  const planKey = showResults ? computePlan(answers) : null;
  const plan = planKey ? PLAN_DETAILS[planKey] : null;

  return (
    <section
      className="px-6 lg:px-10 py-20"
      data-testid="landing-quiz-section"
    >
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-wider text-teal-400 font-bold mb-2">
            30-second check
          </p>
          <h2
            className="text-3xl sm:text-4xl font-bold text-slate-100 leading-tight"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Is InterviewWala <span className="text-teal-400">for you</span>?
          </h2>
          <p className="text-slate-400 mt-2 text-sm">
            4 questions. We'll point you to the right plan.
          </p>
        </div>

        {/* Progress bar */}
        <div className="h-1 rounded-full bg-slate-800 overflow-hidden mb-8">
          <div
            className="h-full bg-gradient-to-r from-teal-400 to-emerald-500 transition-all duration-500"
            style={{ width: `${progress * 100}%` }}
            data-testid="landing-quiz-progress"
          />
        </div>

        {/* Card */}
        <div
          className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6 sm:p-10 min-h-[320px]"
          data-testid="landing-quiz-card"
        >
          {!showResults && (
            <QuestionView
              key={`q-${step}`}
              q={QUESTIONS[step]}
              step={step}
              total={QUESTIONS.length}
              onSelect={handleSelect}
            />
          )}

          {showResults && plan && (
            <ResultsView
              plan={plan}
              planKey={planKey}
              answers={answers}
              onReset={reset}
              onCta={() => {
                if (plan.href.startsWith('mailto:')) {
                  window.location.href = plan.href;
                } else {
                  navigate(plan.href);
                }
              }}
            />
          )}
        </div>
      </div>
    </section>
  );
};

const QuestionView = ({ q, step, total, onSelect }) => {
  const Icon = q.icon;
  return (
    <div data-testid={`landing-quiz-question-${q.id}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-[11px] font-mono text-slate-500">
          Q{step + 1} / {total}
        </span>
      </div>
      <div className="flex items-start gap-3 mb-7">
        <div className="w-10 h-10 rounded-lg bg-teal-500/10 border border-teal-500/30 flex items-center justify-center flex-shrink-0">
          <Icon className="w-5 h-5 text-teal-400" />
        </div>
        <h3
          className="text-xl sm:text-2xl font-bold text-slate-100 leading-snug pt-1.5"
          style={{ fontFamily: 'Manrope, sans-serif' }}
        >
          {q.label}
        </h3>
      </div>

      <ul className="space-y-2">
        {q.options.map((opt) => (
          <li key={opt.value}>
            <button
              type="button"
              onClick={() => onSelect(q.id, opt.value)}
              data-testid={`landing-quiz-option-${q.id}-${opt.value}`}
              className="w-full text-left px-4 py-3 rounded-lg border border-slate-700 bg-slate-800/40 text-slate-200 text-sm font-medium hover:border-teal-500/60 hover:bg-teal-500/5 hover:text-white transition-all duration-200 flex items-center justify-between group"
            >
              <span>{opt.label}</span>
              <ArrowRight className="w-4 h-4 text-slate-600 group-hover:text-teal-400 group-hover:translate-x-0.5 transition-all" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

const ResultsView = ({ plan, planKey, answers, onReset, onCta }) => {
  return (
    <div data-testid="landing-quiz-results">
      <div className="text-center mb-6">
        <div
          className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br ${plan.color} mb-4`}
        >
          <Sparkles className="w-7 h-7 text-slate-900" />
        </div>
        <p className={`text-xs uppercase tracking-wider font-bold mb-1.5 ${plan.accent}`}>
          Recommendation
        </p>
        <h3
          className="text-2xl sm:text-3xl font-bold text-slate-100"
          style={{ fontFamily: 'Manrope, sans-serif' }}
          data-testid="landing-quiz-result-headline"
        >
          {plan.headline}
        </h3>
      </div>

      {/* Reasoning */}
      <div className="space-y-2.5 mb-6 text-sm text-slate-300 leading-relaxed">
        {answers.pain && PAIN_REASONING[answers.pain] && (
          <p
            className="flex items-start gap-2"
            data-testid="landing-quiz-reasoning-pain"
          >
            <CheckCircle2 className="w-4 h-4 text-teal-400 flex-shrink-0 mt-0.5" />
            <span>{PAIN_REASONING[answers.pain]}</span>
          </p>
        )}
        {answers.storage && STORAGE_REASONING[answers.storage] && (
          <p
            className="flex items-start gap-2"
            data-testid="landing-quiz-reasoning-storage"
          >
            <CheckCircle2 className="w-4 h-4 text-teal-400 flex-shrink-0 mt-0.5" />
            <span>{STORAGE_REASONING[answers.storage]}</span>
          </p>
        )}
      </div>

      {/* Plan card */}
      <div
        className={`rounded-xl border ${plan.border} bg-slate-950/40 p-5 mb-6`}
      >
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
            {plan.label} plan
          </span>
          <span
            className={`px-2 py-0.5 text-[10px] font-bold rounded-full bg-gradient-to-r ${plan.color} text-slate-900`}
          >
            BEST FIT
          </span>
        </div>
        <ul className="space-y-1.5">
          {plan.perks.map((p) => (
            <li
              key={p}
              className="text-sm text-slate-200 flex items-start gap-2"
            >
              <CheckCircle2
                className={`w-4 h-4 flex-shrink-0 mt-0.5 ${plan.accent}`}
              />
              <span>{p}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* CTAs */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <Button
          onClick={onCta}
          data-testid="landing-quiz-cta"
          className={`flex-1 h-11 bg-gradient-to-r ${plan.color} text-slate-900 hover:opacity-90 font-semibold`}
        >
          {plan.cta}
          <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
        <button
          type="button"
          onClick={onReset}
          data-testid="landing-quiz-reset"
          className="text-xs text-slate-400 hover:text-slate-100 inline-flex items-center justify-center gap-1 px-3 py-2"
        >
          <RotateCcw className="w-3 h-3" />
          Start over
        </button>
      </div>
    </div>
  );
};

export default LandingQuiz;
